import React, { useEffect, useMemo, useRef, useState } from "react";
import FetchData from "../../providers/dataProvider";
import Spinner from "../common/Spinnner";
import useProductConfigurator from "../../hooks/useProductConfigurator";
import {
  ProductAttributeFilters,
  ProductModelPreview,
  ProductModelSearch,
  ProductSubCategoryTopbar,
} from "./ProductSubCategoryPanels";
import "../../assets/styles/productsubcategory.css";

export const ProductSubCategory = ({ setProductTab, ProductTab }) => {
  const [subProducts, setSubProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchWrapRef = useRef(null);

  const { data: productsubgroups, loading } = FetchData("productsubgroups", {
    groupId: ProductTab?.productSubCategoryId,
  });

  useEffect(() => {
    if (productsubgroups?.length) {
      setSubProducts(productsubgroups);
    }
  }, [productsubgroups]);

  const {
    allColDefs,
    selections,
    matchedProduct,
    breadcrumbParts,
    hasAny,
    getFlatProducts,
    getVisibleColumnStates,
    handlePillClick,
    selectFromSearchResult,
    clearSelections,
    countMatches,
    getMatchingProducts,
  } = useProductConfigurator(subProducts);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const flatProducts = useMemo(
    () => getFlatProducts(subProducts).filter((product) => product.isActive !== false),
    [getFlatProducts, subProducts]
  );

  const searchResults = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return flatProducts;
    return flatProducts.filter((product) =>
      product.productName?.toLowerCase().includes(query)
    );
  }, [flatProducts, searchTerm]);

  const filterGroups = useMemo(
    () => getVisibleColumnStates(subProducts),
    [getVisibleColumnStates, subProducts]
  );

  const matchCount = hasAny ? countMatches(selections, subProducts) : flatProducts.length;

  const candidateProducts =
    hasAny && !matchedProduct ? getMatchingProducts(selections, subProducts) : [];

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleSearchResultClick = (product) => {
    selectFromSearchResult(product, subProducts);
    setSearchTerm(product.productName);
    setShowDropdown(false);
  };

  const handleClear = () => {
    clearSelections();
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleFilterClick = (col, value) => {
    handlePillClick(col, value, subProducts);
  };

  const handleConfigure = () => {
    if (!matchedProduct) return;

    const selectedConfiguration = allColDefs
      .filter((col) => selections[col.key])
      .map((col) => ({
        attributeName: col.attrName,
        valueName: selections[col.key],
      }));

    setProductTab({
      tab: "ProductQuotation",
      id: matchedProduct,
      selectedConfiguration,
      subCategoryId: ProductTab?.subCategoryId,
      productSubCategoryId: ProductTab?.productSubCategoryId,
      title: ProductTab?.title,
      redirectTab: "ProductSubCategory",
    });
  };

  const handleBack = () => {
    setProductTab({ tab: "SubCategory", subCategoryId: ProductTab?.subCategoryId });
  };

  return (
    <div className="psc-page">
      <ProductSubCategoryTopbar title={ProductTab?.title} onBack={handleBack} />

      <div className="row psc-split-row">
        <div className="col-xxl-9 col-xl-9 col-lg-12 col-md-12 col-sm-12 col-xs-12 psc-left-col">
          <ProductModelSearch
            searchTerm={searchTerm}
            searchResults={searchResults}
            showDropdown={showDropdown}
            hasSelection={hasAny}
            searchWrapRef={searchWrapRef}
            onSearchTermChange={(value) => {
              setSearchTerm(value);
              setShowDropdown(true);
            }}
            onFocus={handleSearchFocus}
            onClear={handleClear}
            onResultClick={handleSearchResultClick}
          />

          <ProductAttributeFilters
            filterGroups={filterGroups}
            onPillClick={handleFilterClick}
          />
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-12 col-md-12 col-sm-12 col-xs-12 psc-right-col">
          <div className="psc-preview-col">
            <ProductModelPreview
              matchedProduct={matchedProduct}
              breadcrumbParts={breadcrumbParts}
              hasSelection={hasAny}
              matchCount={matchCount}
              candidateProducts={candidateProducts}
              onConfigure={handleConfigure}
              onCandidateClick={handleSearchResultClick}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="psc-spinner-wrap">
          <Spinner />
        </div>
      )}
    </div>
  );
};
